/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * LayoutSection — Accordion 2: view type thumbnail picker + per-layout options.
 */

import React from 'react';
import { usePublicViewTypes } from '../../../hooks/usePublicViewTypes';

const VIEW_ICONS = {
  card: 'bi-grid-3x3-gap',
  list: 'bi-list-ul',
  table: 'bi-table',
  timeline: 'bi-clock-history',
};

// Per-layout configurable options shown below the picker when a type is selected.
function LayoutOptions({ viewType, config, updateConfig, fields, theme }) {
  const lc      = config.layout_config || {};
  const mapping = config.field_mapping  || {};

  const dateFields = fields.filter(f => f.data_type === 'date' || f.data_type === 'datetime');

  const field = (label, key, input) => (
    <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
      <span className="small text-secondary">{label}</span>
      {input}
    </div>
  );

  const select = (key, options, defaultVal) => (
    <select
      className="form-select form-select-sm"
      style={{ width: 130 }}
      value={lc[key] ?? defaultVal}
      onChange={e => updateConfig({ layout_config: { ...lc, [key]: e.target.value } })}
    >
      {options.map(({ label, value }) => (
        <option key={value} value={value}>{label}</option>
      ))}
    </select>
  );

  // 'custom' is included because a custom LIST layout is still laid out as a
  // grid — the builder writes one card's markup and the page repeats it. Hiding
  // this for custom views meant the only way to set it was the database.
  if (viewType === 'card' || viewType === 'custom') return (
    <div className="mt-3 px-1">
      <div className="small fw-semibold mb-2" style={{ color: theme.primary }}>Card Grid Options</div>
      {field('Cards per row', 'cards_per_row', select('cards_per_row', [
        { label: '2 cards', value: '2' },
        { label: '3 cards', value: '3' },
        { label: '4 cards', value: '4' },
      ], '3'))}
    </div>
  );

  if (viewType === 'list') return null;

  if (viewType === 'table') return null;

  if (viewType === 'timeline') return (
    <div className="mt-3 px-1">
      <div className="small fw-semibold mb-2" style={{ color: theme.primary }}>Timeline Options</div>

      {/* Date field selector — mandatory for timeline ordering */}
      <div className="mb-3">
        <label className="form-label small fw-semibold mb-1">
          Date Field
          <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>
        </label>
        <select
          className="form-select form-select-sm"
          value={mapping.date_field || ''}
          onChange={e => updateConfig({ field_mapping: { ...mapping, date_field: e.target.value || null } })}
        >
          <option value="">— Select a date field —</option>
          {dateFields.map(f => (
            <option key={f.name} value={f.name}>
              {f.display_name || f.name}
            </option>
          ))}
        </select>
        {dateFields.length === 0 && (
          <div className="form-text text-warning mt-1">
            <i className="bi bi-exclamation-triangle me-1" />
            No date or datetime fields found in this table.
          </div>
        )}
        <div className="form-text mt-1" style={{ color: '#64748b' }}>
          This field is mandatory for the Timeline layout. Records will be ordered and displayed based on the selected date field.
        </div>
      </div>

      {field('Sort order', 'timeline_order', select('timeline_order', [
        { label: 'Newest first', value: 'desc' },
        { label: 'Oldest first', value: 'asc' },
      ], 'desc'))}
    </div>
  );

  return null;
}

export default function LayoutSection({ config, updateConfig, fields = [], theme }) {
  const { data: viewTypes = [], isLoading } = usePublicViewTypes();
  const selected = config.view_type || 'card';

  if (isLoading) {
    return <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div>;
  }

  return (
    <div>
      <div className="row g-2">
        {viewTypes.map(vt => {
          const isSelected = selected === vt.key;
          return (
            <div key={vt.key} className="col-6">
              <button
                type="button"
                onClick={() => updateConfig({ view_type: vt.key })}
                className="w-100 p-3 d-flex flex-column align-items-center text-center rounded border"
                style={{
                  background: isSelected ? theme.primary + '15' : '#fff',
                  borderColor: isSelected ? theme.primary : '#e2e8f0',
                  borderWidth: isSelected ? 2 : 1,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <i
                  className={`bi ${VIEW_ICONS[vt.key] || 'bi-layout-text-window'} mb-2`}
                  style={{ fontSize: 28, color: isSelected ? theme.primary : '#64748b' }}
                />
                <div className="fw-semibold small" style={{ color: isSelected ? theme.primary : '#1e293b' }}>
                  {vt.label}
                </div>
                <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                  {vt.description}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <LayoutOptions
        viewType={selected}
        config={config}
        updateConfig={updateConfig}
        fields={fields}
        theme={theme}
      />
    </div>
  );
}
